import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { Volume2, VolumeX, Maximize2, Minimize2, Tv } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createPortal } from 'react-dom';

interface BloombergPlayerProps {
  className?: string;
  hideHeader?: boolean;
}

const BloombergPlayer: React.FC<BloombergPlayerProps> = ({ className, hideHeader = false }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isMuted, setIsMuted] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Bloomberg TV HLS stream URL (public stream)
  const streamUrl = 'https://www.bloomberg.com/media-manifest/streams/us.m3u8';

  useEffect(() => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    setIsLoading(true);
    setHasError(false);

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });

      hlsRef.current = hls;
      hls.loadSource(streamUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        video.play().catch(() => {
          // Autoplay blocked, user needs to interact
        });
      });

      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          setHasError(true);
          setIsLoading(false);
        }
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = streamUrl;
      video.addEventListener('loadedmetadata', () => {
        setIsLoading(false);
        video.play().catch(() => {});
      });
      video.addEventListener('error', () => {
        setHasError(true);
        setIsLoading(false);
      });
    } else {
      setHasError(true);
      setIsLoading(false);
    }
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(!isMuted);
    }
  };

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Controls overlay - minimal when header is hidden
  const controlsOverlay = (
    <div className={cn(
      "absolute z-10 flex items-center gap-1",
      hideHeader ? "bottom-2 right-2" : "top-0 left-0 right-0 bg-gradient-to-b from-black/80 to-transparent px-3 py-2 justify-between"
    )}>
      {!hideHeader && (
        <div className="flex items-center gap-2">
          <Tv size={isExpanded ? 16 : 12} className="text-delphi-red" />
          <span className={cn("font-medium text-delphi-light", isExpanded ? "text-sm" : "text-xs")}>
            Bloomberg TV
          </span>
          {/* Live Indicator */}
          {!isLoading && !hasError && (
            <div className="flex items-center gap-1 ml-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <span className={cn("font-semibold text-red-500 uppercase tracking-wide", isExpanded ? "text-xs" : "text-[10px]")}>
                Live
              </span>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "text-delphi-light/70 hover:text-delphi-light hover:bg-white/10",
            hideHeader ? "h-7 w-7 bg-black/50 rounded-full" : isExpanded ? "h-8 w-8" : "h-6 w-6"
          )}
          onClick={toggleMute}
        >
          {isMuted ? <VolumeX size={hideHeader ? 14 : isExpanded ? 18 : 12} /> : <Volume2 size={hideHeader ? 14 : isExpanded ? 18 : 12} />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "text-delphi-light/70 hover:text-delphi-light hover:bg-white/10",
            hideHeader ? "h-7 w-7 bg-black/50 rounded-full" : isExpanded ? "h-8 w-8" : "h-6 w-6"
          )}
          onClick={toggleExpand}
        >
          {isExpanded ? <Minimize2 size={18} /> : <Maximize2 size={hideHeader ? 14 : 12} />}
        </Button>
      </div>
    </div>
  );

  // The video player content
  const playerContent = (
    <>
      {/* Controls */}
      {controlsOverlay}

      {/* Video */}
      <video
        ref={videoRef}
        className="w-full h-full object-cover bg-black"
        muted={isMuted}
        playsInline
        autoPlay
      />

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-2">
            <div className="w-5 h-5 border-2 border-delphi-red border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-delphi-light/60">Loading...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-2 px-4 text-center">
            <Tv size={20} className="text-delphi-red/60" />
            <span className="text-xs text-delphi-light/60">
              Stream unavailable
            </span>
          </div>
        </div>
      )}
    </>
  );

  // Expanded overlay - uses portal to render at document body level
  if (isExpanded) {
    return createPortal(
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
        onClick={toggleExpand}
      >
        <div 
          className="relative w-[900px] max-w-[90vw] aspect-video bg-delphi-dark border border-delphi-gray/40 rounded-lg overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Full header shown in expanded mode */}
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent px-4 py-3">
            <div className="flex items-center gap-2">
              <Tv size={16} className="text-delphi-red" />
              <span className="font-medium text-delphi-light text-sm">Bloomberg TV</span>
              {!isLoading && !hasError && (
                <div className="flex items-center gap-1 ml-1">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  <span className="font-semibold text-red-500 text-xs uppercase tracking-wide">
                    Live
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-delphi-light/70 hover:text-delphi-light hover:bg-white/10"
                onClick={toggleMute}
              >
                {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-delphi-light/70 hover:text-delphi-light hover:bg-white/10"
                onClick={toggleExpand}
              >
                <Minimize2 size={18} />
              </Button>
            </div>
          </div>
          {/* Video */}
          <video
            ref={videoRef}
            className="w-full h-full object-cover bg-black"
            muted={isMuted}
            playsInline
            autoPlay
          />
          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="flex flex-col items-center gap-2">
                <div className="w-5 h-5 border-2 border-delphi-red border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-delphi-light/60">Loading...</span>
              </div>
            </div>
          )}
          {/* Error State */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80">
              <div className="flex flex-col items-center gap-2 px-4 text-center">
                <Tv size={20} className="text-delphi-red/60" />
                <span className="text-xs text-delphi-light/60">
                  Stream unavailable
                </span>
              </div>
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // Compact view
  return (
    <div className={cn(
      'relative bg-delphi-dark border border-delphi-gray/40 rounded-lg overflow-hidden aspect-video',
      className
    )}>
      {playerContent}
    </div>
  );
};

export default BloombergPlayer;
